import React, { useState, useEffect } from 'react';

import { Button } from '@/components/common';
import CategorySelector from './CategorySelector';
import { CreateCategoryRequest, UpdateCategoryRequest, Category, CategoryStatus, CategoryType, CATEGORY_TYPE_LABELS } from '@/types/category';

interface CategoryFormProps {
  category?: Category;
  onSubmit: (data: CreateCategoryRequest | UpdateCategoryRequest) => void;
  onCancel: () => void;
  onSaveAndClose?: (data: CreateCategoryRequest | UpdateCategoryRequest) => void;
  loading?: boolean;
  disableTypeField?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onSubmit,
  onCancel,
  onSaveAndClose,
  loading = false,
  disableTypeField = false,
}) => {

  const isEditing = !!category;

  const [formData, setFormData] = useState<CreateCategoryRequest>({
    name: '',
    description: '',
    type: 'material_category',
    parentId: '',
    status: 'active',
    for_semi_material: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when category prop changes
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || '',
        type: category.type,
        parentId: category.parentId || '',
        status: category.status,
        for_semi_material: category.for_semi_material || false,
      });
    }
  }, [category]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Tên danh mục là bắt buộc';
    }

    if (!formData.type) {
      newErrors.type = 'Loại danh mục là bắt buộc';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (isEditing) {
      onSubmit({ ...formData, id: category!.id } as UpdateCategoryRequest);
    } else {
      onSubmit(formData);
    }
  };

  const handleSaveAndClose = () => {
    if (!validateForm()) {
      return;
    }

    if (onSaveAndClose) {
      if (isEditing) {
        onSaveAndClose({ ...formData, id: category!.id } as UpdateCategoryRequest);
      } else {
        onSaveAndClose(formData);
      }
    }
  };

  const handleInputChange = (field: keyof CreateCategoryRequest, value: string | boolean) => {
    let processedValue = value;

    // Handle boolean conversion for for_semi_material
    if (field === 'for_semi_material') {
      processedValue = typeof value === 'string' ? value === 'true' : value;
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const statusOptions: { value: CategoryStatus; label: string }[] = [
    { value: 'active', label: 'Đang hoạt động' },
    { value: 'inactive', label: 'Ngừng hoạt động' },
  ];

  const typeOptions: { value: CategoryType; label: string }[] = [
    { value: 'material_category', label: CATEGORY_TYPE_LABELS.material_category },
    { value: 'customer_group', label: CATEGORY_TYPE_LABELS.customer_group },
    { value: 'customer_type', label: CATEGORY_TYPE_LABELS.customer_type },
    { value: 'supplier_group', label: CATEGORY_TYPE_LABELS.supplier_group },
    { value: 'service_type', label: CATEGORY_TYPE_LABELS.service_type },
    { value: 'receipt_voucher_type_customer', label: CATEGORY_TYPE_LABELS.receipt_voucher_type_customer },
    { value: 'receipt_voucher_type_other', label: CATEGORY_TYPE_LABELS.receipt_voucher_type_other },
  ];

  // Generate display values for readonly fields
  const categoryCode = category?.code || 'Tự động tạo';
  const createdBy = category?.creator || 'Người dùng hiện tại';
  const creationDate = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Top Row - Category Code (readonly) and Created By (readonly) as specified */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Category Code - top-left as specified */}
        <div>
          <label htmlFor="categoryCode" className="block text-sm font-medium text-gray-700 mb-1">
            Mã danh mục
          </label>
          <input
            type="text"
            id="categoryCode"
            value={categoryCode}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            placeholder="Mã danh mục"
          />
        </div>

        {/* Created By - top-right, horizontally aligned with Category Code as specified */}
        <div>
          <label htmlFor="createdBy" className="block text-sm font-medium text-gray-700 mb-1">
            Người tạo
          </label>
          <input
            type="text"
            id="createdBy"
            value={createdBy}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            placeholder="Người tạo"
          />
        </div>
      </div>

      {/* Middle Section - Category Name, Type and Creation Date as specified */}
      <div className="space-y-4">
        {/* Category Name - below Category Code, full-width field as specified */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Tên danh mục *
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`input-field ${errors.name ? 'border-red-500' : ''}`}
            placeholder="Nhập tên danh mục"
            disabled={loading}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Category Type */}
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
            Loại danh mục *
          </label>
          <select
            id="type"
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value as CategoryType)}
            className={`input-field ${errors.type ? 'border-red-500' : ''}`}
            disabled={loading || disableTypeField}
          >
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.type && <p className="text-red-500 text-xs mt-1">{errors.type}</p>}
        </div>

        {/* Parent Category */}
        <div>
          <label htmlFor="parentId" className="block text-sm font-medium text-gray-700 mb-1">
            Danh mục cha
          </label>
          <CategorySelector
            value={formData.parentId}
            onChange={(categoryId) => handleInputChange('parentId', categoryId)}
            placeholder="Chọn danh mục cha (tùy chọn)"
            disabled={loading}
            excludeId={category?.id} // Prevent circular reference
            typeFilter={[formData.type]} // Only show same type categories
          />
          <p className="text-xs text-gray-500 mt-1">
            Để trống nếu đây là danh mục gốc
          </p>
        </div>

        {/* Creation Date - directly under Category Name as specified */}
        <div>
          <label htmlFor="creationDate" className="block text-sm font-medium text-gray-700 mb-1">
            Ngày tạo
          </label>
          <input
            type="date"
            id="creationDate"
            value={category?.createdDate || creationDate}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
            Trạng thái *
          </label>
          <select
            id="status"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value as CategoryStatus)}
            className="input-field"
            disabled={loading}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Semi-material flag */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="for_semi_material"
            checked={formData.for_semi_material}
            onChange={(e) => handleInputChange('for_semi_material', e.target.checked)}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="for_semi_material" className="ml-2 block text-sm text-gray-700">
            Dành cho bán thành phẩm
          </label>
        </div>
      </div>

      {/* Description Textarea - placed at the bottom, spans full modal width as specified */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Mô tả
        </label>
        <textarea
          id="description"
          rows={4}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          className="input-field resize-none"
          placeholder="Nhập mô tả cho danh mục..."
          disabled={loading}
        />
      </div>

      {/* Action Buttons - Save and Save & Close positioned bottom-right as specified */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Hủy
        </Button>
        
        {/* Save Button - located at bottom-right as specified */}
        <Button
          type="submit"
          variant="outline"
          loading={loading}
        >
          {isEditing ? 'Cập nhật' : 'Lưu'}
        </Button>
        
        {/* Save and Close Button - positioned to the right of Save button, bottom-right corner as specified */}
        {onSaveAndClose && (
          <Button
            type="button"
            variant="primary"
            onClick={handleSaveAndClose}
            loading={loading}
          >
            {isEditing ? 'Cập nhật và đóng' : 'Lưu và đóng'}
          </Button>
        )}
      </div>
    </form>
  );
};

export default CategoryForm;
